import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ExternalLink, Loader2 } from "lucide-react";
import ShareCard from "@/components/ShareCard";
import { API } from "@/lib/api";

/**
 * PublicShare — public, no-auth page rendering a shared Q&A snapshot.
 * URL: /s/:token. Fetches GET /api/shares/:token and renders it with
 * the same ShareCard layout users see in the modal, plus a CTA to try
 * HBM themselves. This is the URL that gets posted to LinkedIn, X, etc.
 */
export default function PublicShare() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, err: null, data: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/shares/${encodeURIComponent(token)}`);
        if (!r.ok) throw new Error(r.status === 404 ? "Share not found." : `HTTP ${r.status}`);
        const data = await r.json();
        if (!cancelled) setState({ loading: false, err: null, data });
      } catch (e) {
        if (!cancelled) setState({ loading: false, err: e.message || "Failed to load.", data: null });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // EU AI Act Article 50: inject <meta> tags marking this page as
  // AI-generated content so crawlers, browsers, and 3rd-party detectors
  // can identify it without parsing the body.
  useEffect(() => {
    const tags = [
      { name: "ai-generated", content: "true" },
      { name: "ai-content-source", content: "Humane Behavior AI" },
      { name: "ai-content-disclosure", content: "EU AI Act Article 50" },
    ];
    const inserted = tags.map((t) => {
      const el = document.createElement("meta");
      el.name = t.name;
      el.content = t.content;
      document.head.appendChild(el);
      return el;
    });
    return () => inserted.forEach((el) => el.remove());
  }, []);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[var(--bg-elevated)] flex items-center justify-center text-[var(--text)] font-mono-ui text-[11px] tracking-[0.3em] uppercase">
        <Loader2 className="animate-spin mr-3" size={16} /> Loading share…
      </div>
    );
  }
  if (state.err || !state.data) {
    return (
      <div className="min-h-screen bg-[var(--bg-elevated)] flex flex-col items-center justify-center text-[var(--text)] gap-6 px-6">
        <div className="font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[var(--text)]/70">
          {state.err || "Share not found."}
        </div>
        <Link
          to="/"
          data-testid="public-share-home"
          className="inline-flex items-center gap-2 border border-[var(--border)] hover:border-[var(--border)] px-4 py-2 font-mono-ui text-[10px] tracking-[0.24em] uppercase transition-colors"
        >
          Go to humanebehavior.com
        </Link>
      </div>
    );
  }

  const { question, answer, view_count, created_at } = state.data;

  return (
    <div className="min-h-screen bg-[var(--bg-elevated)] flex flex-col items-center py-10 px-4" data-testid="public-share-page">
      {/* EU AI Act Art. 50 disclosure banner */}
      <div
        data-testid="ai-generated-banner"
        className="w-full max-w-4xl mb-4 border-l-2 border-[var(--accent)] pl-3 py-1.5"
      >
        <p className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--accent)]">
          AI-generated content — reply produced by Humane Behavior AI
        </p>
      </div>

      {/* Header — brand + view count + CTA */}
      <div className="w-full max-w-4xl flex items-center justify-between mb-6">
        <Link
          to="/"
          className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[var(--text)] hover:opacity-80 transition-opacity"
        >
          Humane Behavior AI
        </Link>
        <div className="flex items-center gap-4">
          <span className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text)]/60">
            {view_count} view{view_count === 1 ? "" : "s"}
          </span>
          <Link
            to="/"
            data-testid="public-share-cta"
            className="inline-flex items-center gap-2 border border-[var(--border)] hover:border-[var(--border)] px-3 py-1.5 font-mono-ui text-[10px] tracking-[0.24em] uppercase transition-colors"
          >
            Ask HBM <ExternalLink size={11} />
          </Link>
        </div>
      </div>

      {/* Card */}
      <div className="w-full max-w-4xl">
        <ShareCard question={question} answer={answer} />
      </div>

      {/* Footer strapline */}
      <div className="max-w-2xl mt-8 text-center">
        <p className="font-serif text-lg text-[var(--text)]/90 leading-snug italic">
          &ldquo;The answer textbooks lie about.&rdquo;
        </p>
        <p className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text)]/60 mt-3">
          Get your own read at humanebehavior.com
        </p>
        {created_at && (
          <p className="font-mono-ui text-[9px] tracking-[0.18em] uppercase text-[var(--text)]/40 mt-4">
            Shared {new Date(created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
          </p>
        )}
      </div>
    </div>
  );
}
